/**
 * Edit Credits Dialog
 *
 * Modal for editing user AI credits, monthly limit, and subscription tier
 */
import { useState, useEffect } from "react";
import { useSnapshot } from "valtio";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, RotateCcw, DollarSign } from "lucide-react";
import { toast } from "sonner";
import {
  aiCreditsStore,
  closeEditDialog,
  updateUserCredits,
  resetMonthlyUsage,
  topUpCredits,
  TIER_DEFAULTS,
  type SubscriptionTier,
} from "@/features/admin/stores/ai-credits";

export function EditCreditsDialog() {
  const { editingUser, editDialogOpen, actionLoading } =
    useSnapshot(aiCreditsStore);

  // Form state
  const [creditsRemaining, setCreditsRemaining] = useState("");
  const [monthlyLimit, setMonthlyLimit] = useState("");
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>("free");

  // Reset form when user changes
  useEffect(() => {
    if (editingUser) {
      setCreditsRemaining((editingUser.credits_remaining ?? 0).toFixed(2));
      setMonthlyLimit((editingUser.monthly_limit ?? 0).toFixed(2));
      setSubscriptionTier(editingUser.subscription_tier || "free");
    }
  }, [editingUser]);

  const handleTierChange = (tier: SubscriptionTier) => {
    setSubscriptionTier(tier);
    // Auto-update monthly limit to tier default
    const defaults = TIER_DEFAULTS[tier];
    setMonthlyLimit(defaults.monthly_limit.toFixed(2));
  };

  const handleSave = async () => {
    if (!editingUser) return;

    const credits = parseFloat(creditsRemaining);
    const limit = parseFloat(monthlyLimit);

    if (isNaN(credits) || credits < 0) {
      toast.error("Credits must be a non-negative number");
      return;
    }

    if (isNaN(limit) || limit <= 0) {
      toast.error("Monthly limit must be a positive number");
      return;
    }

    try {
      await updateUserCredits(editingUser.id, {
        credits_remaining: credits,
        monthly_limit: limit,
        subscription_tier: subscriptionTier,
      });
      toast.success("User credits updated");
    } catch {
      toast.error("Failed to update credits");
    }
  };

  const handleResetUsage = async () => {
    if (!editingUser) return;

    try {
      await resetMonthlyUsage(editingUser.id);
      toast.success("Monthly usage reset to $0.00");
    } catch {
      toast.error("Failed to reset usage");
    }
  };

  const handleTopUp = async (amount: number) => {
    if (!editingUser) return;

    try {
      await topUpCredits(editingUser.id, amount);
      // Update local state to reflect the change
      const newBalance = parseFloat(creditsRemaining) + amount;
      setCreditsRemaining(newBalance.toFixed(2));
      toast.success(`Added $${amount.toFixed(2)} credits`);
    } catch {
      toast.error("Failed to add credits");
    }
  };

  if (!editingUser) return null;

  return (
    <Dialog open={editDialogOpen} onOpenChange={(open) => !open && closeEditDialog()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User Credits</DialogTitle>
          <p className="text-sm text-muted-foreground">
            User: <code className="rounded bg-muted px-1">{editingUser.id}</code>
          </p>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Subscription Tier */}
          <div className="space-y-2">
            <Label htmlFor="tier">Subscription Tier</Label>
            <Select value={subscriptionTier} onValueChange={handleTierChange}>
              <SelectTrigger id="tier">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Credits Remaining */}
          <div className="space-y-2">
            <Label htmlFor="credits">Credits Remaining</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="credits"
                type="number"
                step="0.01"
                min="0"
                value={creditsRemaining}
                onChange={(e) => setCreditsRemaining(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Quick Top-Up Buttons */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Quick Top-Up</Label>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTopUp(10)}
                disabled={actionLoading}
              >
                +$10
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTopUp(50)}
                disabled={actionLoading}
              >
                +$50
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTopUp(100)}
                disabled={actionLoading}
              >
                +$100
              </Button>
            </div>
          </div>

          {/* Monthly Limit */}
          <div className="space-y-2">
            <Label htmlFor="limit">Monthly Limit</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="limit"
                type="number"
                step="0.01"
                min="0.01"
                value={monthlyLimit}
                onChange={(e) => setMonthlyLimit(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Used This Month */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground">Used This Month</Label>
              <span className="text-sm font-medium">
                ${(editingUser.credits_used_this_month ?? 0).toFixed(2)}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleResetUsage}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Reset Monthly Usage
            </Button>
          </div>

          {/* Last Reset */}
          <div className="text-sm text-muted-foreground">
            Last reset: {editingUser.last_credit_reset
              ? new Date(editingUser.last_credit_reset).toLocaleDateString()
              : "-"}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={closeEditDialog}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={actionLoading}>
            {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
